import React, { useMemo } from 'react'
import {
  ArrowRight,
  Building2,
  ClipboardList,
  Eye,
  FileSearch,
  Flame,
  LogIn,
  MessageCircleMore,
  Send,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { asArray, getUsageDailyTrendPoints } from '../lib/opsUsageSummary'

const STATUS_LABELS = {
  ACTIVE: '활성',
  TRIALING: '체험 중',
  TRIAL: '체험 중',
  PAST_DUE: '결제 지연',
  CANCELED: '취소',
  CANCELLED: '취소',
  INCOMPLETE: '결제 미완료',
  INCOMPLETE_EXPIRED: '결제 미완료 만료',
  UNPAID: '미납',
  PAUSED: '일시 중지',
  EXPIRED: '만료',
}

const MENU_LABELS = {
  MENU_HOME: '홈',
  MENU_CATEGORY_LANDING: '홍보용페이지',
  MENU_SALES_IMAGES: '이미지 자료실',
  MENU_SALES_PUBLIC_CARD: '모바일명함',
  MENU_SALES_PUBLIC_LANDING: '개인홈페이지',
  MENU_SALES_ORG_HOMEPAGE: '조직홈페이지',
  MENU_SALES_CONSULTATIONS: 'DB수집관리',
  MENU_HEALTH: '진료정보분석',
  MENU_FIRE: '화재보험설계',
  MENU_REQUESTS: '설계관리',
  MENU_NOTICES: '공지',
  MENU_SUPPORT_BULK_SMS: '단체문자발송',
  MENU_SUPPORT_KAKAO_BULK: '카카오톡 단체발송',
  MENU_SUPPORT_SURVEYS: '설문관리',
  MENU_SUPPORT_CUSTOMERS: '고객관리',
  MENU_SUPPORT_INQUIRIES: '고객문의',
  MENU_SUPPORT_CLAIMS: '보험금청구',
  MENU_WORK_INSURANCE_NEWS: '보험뉴스',
  MENU_WORK_INSURER_NEWSLETTERS: '보험사 소식지',
  MENU_WORK_INSURER_SYSTEMS: '보험사전산',
  MENU_WORK_CALENDAR: '일정관리',
  MENU_WORK_POLICY_LOOKUP: '약관조회',
  MENU_ORG_DASHBOARD: '조직 대시보드',
  MENU_REPORTS: '통계·리포트',
  MENU_ORGS: '조직관리',
  MENU_USERS: '조직 사용자',
  MENU_CUSTOMERS: '고객업무 허브',
  MENU_UW_EXCEPTIONS: '예외질환조회',
  MENU_USER_TASKS: '오늘의 업무',
  MENU_OTHER: '기타 메뉴',
}

const PUBLIC_SURFACES = [
  { key: 'publicCard', label: '모바일명함' },
  { key: 'personalLanding', label: '개인홈페이지' },
  { key: 'orgLanding', label: '조직홈페이지' },
  { key: 'categoryLanding', label: '홍보용페이지' },
]

const DB_SURFACES = [
  { key: 'personalLanding', label: '개인홈페이지' },
  { key: 'orgLanding', label: '조직홈페이지' },
  { key: 'categoryLanding', label: '홍보용페이지' },
]

function valueOf(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function count(value) {
  return valueOf(value).toLocaleString('ko-KR')
}

function percent(value, digits = 1) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? `${parsed.toFixed(digits)}%` : '집계 없음'
}

function ratio(numerator, denominator) {
  const bottom = valueOf(denominator)
  return bottom > 0 ? (valueOf(numerator) / bottom) * 100 : null
}

function sum(source, keys) {
  return keys.reduce((total, key) => total + valueOf(source?.[key]), 0)
}

function Metric({ icon: Icon, label, value, detail, tone = 'blue' }) {
  const toneClass = {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    violet: 'bg-violet-50 text-violet-700',
    amber: 'bg-amber-50 text-amber-700',
    rose: 'bg-rose-50 text-rose-700',
    cyan: 'bg-cyan-50 text-cyan-700',
  }[tone]
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
      <div className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass}`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <p className="mt-3 text-xs font-semibold text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{value}</p>
      <p className="mt-1 min-h-5 text-xs text-slate-500">{detail}</p>
    </div>
  )
}

function Panel({ title, description, children, className = '' }) {
  return (
    <section className={`space-y-3 ${className}`}>
      <div>
        <h2 className="text-base font-bold text-slate-950">{title}</h2>
        {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">{children}</div>
    </section>
  )
}

function EmptyChart() {
  return <div className="flex h-full items-center justify-center text-sm text-slate-500">집계된 데이터가 없습니다.</div>
}

function StatusDistribution({ title, values }) {
  const rows = Object.entries(values || {})
    .map(([key, itemCount]) => ({ key, label: STATUS_LABELS[key] || '기타 상태', count: valueOf(itemCount) }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count)
  const total = rows.reduce((result, item) => result + item.count, 0)
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <span className="text-sm font-bold tabular-nums text-slate-700">{count(total)}건</span>
      </div>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">구독 데이터가 없습니다.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {rows.map((row) => (
            <div key={row.key}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-slate-600">{row.label}</span>
                <span className="font-bold tabular-nums text-slate-900">{count(row.count)}</span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-blue-600" style={{ width: `${total > 0 ? (row.count / total) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function OpsMembersStatsPanel({ data, period }) {
  const rolling = data?.rolling?.[period] || {}
  const activeMembers = valueOf(data?.membersActive)
  const loginsToday = valueOf(data?.distinctLoginsToday)
  const activeByOrgType = data?.membersActiveByOrgType || {}
  const personalMembers = valueOf(activeByOrgType.PERSONAL)
  const organizationMembers = Object.entries(activeByOrgType)
    .filter(([key]) => key !== 'PERSONAL')
    .reduce((total, [, itemCount]) => total + valueOf(itemCount), 0)
  const unclassifiedMembers = Math.max(0, activeMembers - personalMembers - organizationMembers)
  const memberRows = [
    { label: '개인 회원', count: personalMembers, color: 'bg-blue-600' },
    { label: '조직 소속 회원', count: organizationMembers, color: 'bg-emerald-600' },
    { label: '소속 미분류', count: unclassifiedMembers, color: 'bg-slate-400' },
  ].filter((row) => row.count > 0)
  const daily = getUsageDailyTrendPoints(data?.dailyTrendKst)
  const periodLabel = period === '1' ? '오늘' : `최근 ${period}일`

  return (
    <div className="space-y-7">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric icon={Users} label="활성 회원" value={`${count(activeMembers)}명`} detail="탈퇴하지 않은 활성 계정" tone="blue" />
        <Metric icon={LogIn} label="로그인 사용자" value={`${count(rolling.distinctLoginUsers)}명`} detail={`${periodLabel} 고유 사용자`} tone="violet" />
        <Metric icon={UserPlus} label="신규 가입" value={`${count(rolling.signupUsers)}명`} detail={`${periodLabel} 생성 계정`} tone="emerald" />
        <Metric icon={Sparkles} label="오늘 로그인률" value={percent(ratio(loginsToday, activeMembers))} detail={`오늘 ${count(loginsToday)}명 로그인`} tone="amber" />
      </div>

      <Panel title="가입·로그인 추세" description="최근 30일 일별 신규 가입과 고유 로그인 사용자입니다.">
        <div className="h-80 min-h-[260px]">
          {daily.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="statDate" tick={{ fontSize: 11 }} tickFormatter={(item) => String(item || '').slice(5).replace('-', '/')} minTickGap={18} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 6, borderColor: '#cbd5e1', fontSize: 12 }} formatter={(item, name) => [count(item), name === 'signupUsers' ? '신규 가입' : '로그인 사용자']} />
                <Line type="monotone" dataKey="signupUsers" stroke="#059669" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="distinctLoginUsers" stroke="#2563eb" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Panel>

      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <Panel title="회원 소속 구성" description="GA·원수사 구분 없이 개인과 조직 소속 기준으로 집계합니다.">
          <div className="space-y-4">
            {memberRows.map((row) => (
              <div key={row.label}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{row.label}</span>
                  <span className="font-bold tabular-nums text-slate-950">{count(row.count)}명 · {percent(ratio(row.count, activeMembers))}</span>
                </div>
                <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full ${row.color}`} style={{ width: `${ratio(row.count, activeMembers) || 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="구독 상태" description="개인 결제와 조직 계약의 최신 구독 상태입니다.">
          <div className="grid gap-7 sm:grid-cols-2">
            <StatusDistribution title="개인 구독" values={data?.subscriptionsLatestByStatus?.USER} />
            <StatusDistribution title="조직 계약" values={data?.subscriptionsLatestByStatus?.ORG} />
          </div>
        </Panel>
      </div>
    </div>
  )
}

export function OpsServiceStatsPanel({ data, period }) {
  const rolling = data?.rolling?.[period] || {}
  const daily = useMemo(() => getUsageDailyTrendPoints(data?.dailyTrendKst).map((row) => ({
    ...row,
    serviceTotal: sum(row, ['requestsCreated', 'healthReportsCreated', 'fireBuildingLookups', 'counselSurveySessionsCreated']),
  })), [data?.dailyTrendKst])
  const menuRows = [...asArray(data?.pageviewsByMenu?.[period])]
    .sort((a, b) => valueOf(b?.clicks) - valueOf(a?.clicks))
    .slice(0, 10)
  const menuMax = Math.max(1, ...menuRows.map((row) => valueOf(row?.clicks)))
  const channels = [
    { label: '웹 PC', value: rolling.pageviewsWebDesktop, color: 'bg-blue-600' },
    { label: '웹 모바일', value: rolling.pageviewsWebMobile, color: 'bg-cyan-600' },
    { label: '앱 Android', value: rolling.pageviewsAppAndroid, color: 'bg-emerald-600' },
    { label: '앱 iOS', value: rolling.pageviewsAppIos, color: 'bg-violet-600' },
    { label: '기타·미분류', value: sum(rolling, ['pageviewsWebUnknown', 'pageviewsAppUnknown']), color: 'bg-slate-400' },
  ].filter((row) => valueOf(row.value) > 0)
  const totalPageviews = valueOf(rolling.pageviewsTotal)

  return (
    <div className="space-y-7">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Metric icon={ClipboardList} label="설계의뢰" value={`${count(rolling.requestsCreated)}건`} detail={`최근 ${period}일 생성`} tone="blue" />
        <Metric icon={FileSearch} label="진료정보분석" value={`${count(rolling.healthReportsCreated)}건`} detail={`최근 ${period}일 리포트 생성`} tone="violet" />
        <Metric icon={Flame} label="화재 건축물 조회" value={`${count(rolling.fireBuildingLookups)}건`} detail={`최근 ${period}일 조회`} tone="rose" />
        <Metric icon={MessageCircleMore} label="상담 설문" value={`${count(rolling.counselSurveySessionsCreated)}건`} detail={`최근 ${period}일 세션 생성`} tone="emerald" />
        <Metric icon={Send} label="카카오·문자 알림" value={`${count(rolling.notificationKakaoFamily)}건`} detail={`최근 ${period}일 발송 집계`} tone="amber" />
        <Metric icon={Eye} label="제품 페이지뷰" value={`${count(totalPageviews)}회`} detail="운영자 화면 제외" tone="cyan" />
      </div>

      <Panel title="서비스 이용 추세" description="최근 30일 주요 업무 기능의 일별 실행량입니다.">
        <div className="h-80 min-h-[260px]">
          {daily.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="statDate" tick={{ fontSize: 11 }} tickFormatter={(item) => String(item || '').slice(5).replace('-', '/')} minTickGap={18} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 6, borderColor: '#cbd5e1', fontSize: 12 }}
                  formatter={(item, name) => [count(item), {
                    requestsCreated: '설계의뢰',
                    healthReportsCreated: '진료정보분석',
                    fireBuildingLookups: '화재 건축물 조회',
                    counselSurveySessionsCreated: '상담 설문',
                  }[name] || '이용량']}
                />
                <Line type="monotone" dataKey="requestsCreated" stroke="#2563eb" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="healthReportsCreated" stroke="#7c3aed" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="fireBuildingLookups" stroke="#e11d48" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="counselSurveySessionsCreated" stroke="#059669" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Panel>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="이용 채널" description="제품 페이지뷰의 웹·앱 채널 구성입니다.">
          {channels.length === 0 ? <p className="text-sm text-slate-500">채널 데이터가 없습니다.</p> : (
            <div className="space-y-4">
              {channels.map((row) => (
                <div key={row.label}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{row.label}</span>
                    <span className="font-bold tabular-nums text-slate-950">{count(row.value)} · {percent(ratio(row.value, totalPageviews))}</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full ${row.color}`} style={{ width: `${ratio(row.value, totalPageviews) || 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="메뉴 이용 순위" description="제품 메뉴 페이지뷰 상위 10개입니다.">
          {menuRows.length === 0 ? <p className="text-sm text-slate-500">메뉴 데이터가 없습니다.</p> : (
            <ol className="space-y-3">
              {menuRows.map((row, index) => (
                <li key={`${row.menuKey}-${index}`} className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">{index + 1}</span>
                  <div className="min-w-0">
                    <span className="block truncate text-sm font-medium text-slate-700">{MENU_LABELS[row.menuKey] || '기타 메뉴'}</span>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full bg-slate-700" style={{ width: `${(valueOf(row.clicks) / menuMax) * 100}%` }} />
                    </div>
                  </div>
                  <span className="text-sm font-bold tabular-nums text-slate-950">{count(row.clicks)}</span>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>
    </div>
  )
}

export function OpsAcquisitionStatsPanel({ data, period }) {
  const publicViews = data?.publicLinkViews?.[period] || {}
  const dbStats = data?.dbCollectionStats?.[period] || {}
  const publicTotal = Object.values(publicViews).reduce((total, item) => total + valueOf(item), 0)
  const aggregate = DB_SURFACES.reduce((result, surface) => {
    const bucket = dbStats?.[surface.key] || {}
    return {
      views: result.views + valueOf(bucket.views),
      requests: result.requests + valueOf(bucket.consultRequests),
      completed: result.completed + valueOf(bucket.consultCompleted),
    }
  }, { views: 0, requests: 0, completed: 0 })
  const publicMax = Math.max(1, ...PUBLIC_SURFACES.map((surface) => valueOf(publicViews?.[surface.key])))

  return (
    <div className="space-y-7">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric icon={Eye} label="공개 링크 조회" value={`${count(publicTotal)}회`} detail={`최근 ${period}일 전체 공개 페이지`} tone="blue" />
        <Metric icon={Building2} label="DB 수집 페이지 조회" value={`${count(aggregate.views)}회`} detail="상담 신청이 가능한 페이지" tone="cyan" />
        <Metric icon={MessageCircleMore} label="상담 신청" value={`${count(aggregate.requests)}건`} detail={`조회 대비 ${percent(ratio(aggregate.requests, aggregate.views))}`} tone="amber" />
        <Metric icon={Sparkles} label="상담 완료" value={`${count(aggregate.completed)}건`} detail={`신청 대비 ${percent(ratio(aggregate.completed, aggregate.requests))}`} tone="emerald" />
      </div>

      <Panel title="전체 전환 흐름" description="DB 수집 페이지 조회에서 상담 완료까지의 누적 흐름입니다.">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { label: '페이지 조회', value: aggregate.views, rate: 100, color: 'bg-blue-600' },
            { label: '상담 신청', value: aggregate.requests, rate: ratio(aggregate.requests, aggregate.views), color: 'bg-amber-500' },
            { label: '상담 완료', value: aggregate.completed, rate: ratio(aggregate.completed, aggregate.views), color: 'bg-emerald-600' },
          ].map((step, index) => (
            <div key={step.label} className="relative">
              <p className="text-xs font-semibold text-slate-500">{step.label}</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{count(step.value)}</p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full ${step.color}`} style={{ width: `${Math.min(100, step.rate || 0)}%` }} />
              </div>
              <p className="mt-1 text-xs text-slate-500">최초 조회 대비 {index === 0 ? '100.0%' : percent(step.rate)}</p>
              {index < 2 ? <ArrowRight className="absolute -right-3 top-8 hidden h-4 w-4 text-slate-300 sm:block" aria-hidden="true" /> : null}
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <Panel title="공개 페이지별 조회" description="외부에서 열린 공개 링크의 조회량입니다.">
          <div className="space-y-4">
            {PUBLIC_SURFACES.map((surface) => {
              const itemValue = valueOf(publicViews?.[surface.key])
              return (
                <div key={surface.key}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{surface.label}</span>
                    <span className="font-bold tabular-nums text-slate-950">{count(itemValue)}</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full bg-blue-600" style={{ width: `${(itemValue / publicMax) * 100}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </Panel>

        <Panel title="페이지별 상담 전환" description="페이지 특성별 신청·완료 전환을 비교합니다.">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500">
                  <th className="px-2 py-3">페이지</th>
                  <th className="px-2 py-3 text-right">조회</th>
                  <th className="px-2 py-3 text-right">상담 신청</th>
                  <th className="px-2 py-3 text-right">신청 전환율</th>
                  <th className="px-2 py-3 text-right">상담 완료</th>
                  <th className="px-2 py-3 text-right">완료율</th>
                </tr>
              </thead>
              <tbody>
                {DB_SURFACES.map((surface) => {
                  const bucket = dbStats?.[surface.key] || {}
                  return (
                    <tr key={surface.key} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-3 font-semibold text-slate-800">{surface.label}</td>
                      <td className="px-2 py-3 text-right tabular-nums">{count(bucket.views)}</td>
                      <td className="px-2 py-3 text-right tabular-nums">{count(bucket.consultRequests)}</td>
                      <td className="px-2 py-3 text-right tabular-nums">{percent(bucket.viewToRequestRate)}</td>
                      <td className="px-2 py-3 text-right tabular-nums">{count(bucket.consultCompleted)}</td>
                      <td className="px-2 py-3 text-right tabular-nums">{percent(bucket.requestToCompletedRate)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  )
}